import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApi } from '@/contexts/ApiContext';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useToast } from '@/contexts/ToastContext';

// Types
interface PurchaseOrderNoteUpdateFormData {
  transportationCost: number;
  packagingCost: number;
  miscellaneousCost: number;
  loadingPoint: string;
  deliveryPoint: string;
  deliveryNote: string;
}

interface PurchaseOrderNoteUpdateSheetProps {
  purchaseOrder: {
    _id: string;
    purchaseOrderNumber: string;
    supplier?: {
      businessName: string;
      contactName: string;
    };
    items?: Array<{
      inventory: { name: string; measurementUnit?: string };
      quantity: number;
      unitPrice: number;
    }>;
    totalAmount?: number;
    note?: {
      transportationCost?: number;
      packagingCost?: number;
      miscCost?: number;
      loadingPoint?: string;
      deliveryPoint?: string;
      deliveryNote?: string;
    };
  };
  onNoteUpdated?: () => Promise<void>;
}

const PurchaseOrderNoteUpdateSheet: React.FC<
  PurchaseOrderNoteUpdateSheetProps
> = ({ purchaseOrder, onNoteUpdated }) => {
  const api = useApi();
  const { hideSheet } = useGlobalState();
  const { showSuccess, showError } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<PurchaseOrderNoteUpdateFormData>({
    defaultValues: {
      transportationCost: purchaseOrder.note?.transportationCost || 0,
      packagingCost: purchaseOrder.note?.packagingCost || 0,
      miscellaneousCost: purchaseOrder.note?.miscCost || 0,
      loadingPoint: purchaseOrder.note?.loadingPoint || '',
      deliveryPoint: purchaseOrder.note?.deliveryPoint || '',
      deliveryNote: purchaseOrder.note?.deliveryNote || '',
    },
    mode: 'onChange',
  });

  const watchedValues = watch();

  const handleSubmitForm = async (
    formData: PurchaseOrderNoteUpdateFormData
  ) => {
    setIsLoading(true);
    try {
      const noteData = {
        transportationCost: formData.transportationCost,
        packagingCost: formData.packagingCost,
        miscCost: formData.miscellaneousCost,
        loadingPoint: formData.loadingPoint,
        deliveryPoint: formData.deliveryPoint,
        deliveryNote: formData.deliveryNote,
      };

      const response = await api.put(`/purchase-order/${purchaseOrder._id}`, {
        note: noteData,
      });

      if (response.success) {
        showSuccess(
          response.message || 'Purchase order note updated successfully'
        );
        hideSheet('edit-purchase-order-note');

        if (onNoteUpdated) {
          await onNoteUpdated();
        }
      } else {
        showError(
          response.error?.message || 'Failed to update purchase order note'
        );
      }
    } catch (error) {
      showError('An error occurred while updating the purchase order note');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    reset();
    hideSheet('edit-purchase-order-note');
  };

  // Expose handlers to global window object for GlobalSheetRenderer
  useEffect(() => {
    const handlers = {
      handleSubmit: () => handleSubmit(handleSubmitForm)(),
      handleCancel,
      isLoading,
    };

    // Store handlers globally
    if (!(window as any).purchaseOrderNoteUpdateHandlers) {
      (window as any).purchaseOrderNoteUpdateHandlers = {};
    }
    (window as any).purchaseOrderNoteUpdateHandlers = handlers;

    // Cleanup on unmount
    return () => {
      if ((window as any).purchaseOrderNoteUpdateHandlers) {
        delete (window as any).purchaseOrderNoteUpdateHandlers;
      }
    };
  }, [isLoading, handleSubmit, handleSubmitForm]);

  const onSubmit = async (formData: PurchaseOrderNoteUpdateFormData) => {
    await handleSubmitForm(formData);
  };

  const calculateTotalCosts = () => {
    return (
      watchedValues.transportationCost +
      watchedValues.packagingCost +
      watchedValues.miscellaneousCost
    );
  };

  const calculateGrandTotal = () => {
    return (purchaseOrder.totalAmount || 0) + calculateTotalCosts();
  };

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString()}`;
  };

  return (
    <div className='space-y-6'>
      {/* Purchase Order Info */}
      <div className='bg-gray-5 p-4 rounded-lg'>
        <h4 className='text-sm font-medium text-gray-90 mb-3'>
          Purchase Order Information
        </h4>
        <div className='space-y-2 text-sm'>
          <div className='flex justify-between'>
            <span className='text-gray-60'>PO Number:</span>
            <span className='font-mono font-semibold'>
              {purchaseOrder.purchaseOrderNumber}
            </span>
          </div>
          {purchaseOrder.supplier && (
            <div className='flex justify-between'>
              <span className='text-gray-60'>Supplier:</span>
              <span className='font-medium'>
                {purchaseOrder.supplier.businessName}
              </span>
            </div>
          )}
          <div className='flex justify-between'>
            <span className='text-gray-60'>Items:</span>
            <span>{purchaseOrder.items?.length || 0} items</span>
          </div>
          {purchaseOrder.totalAmount && (
            <div className='flex justify-between font-semibold border-t pt-2'>
              <span>Order Amount:</span>
              <span>{formatCurrency(purchaseOrder.totalAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Current Note Information */}
      {purchaseOrder.note && (
        <div className='bg-brand-5 p-4 rounded-lg'>
          <h4 className='text-sm font-medium text-brand-90 mb-3'>
            Current Note Information
          </h4>
          <div className='space-y-2 text-sm text-brand-80'>
            <div className='flex justify-between'>
              <span>Transportation Cost:</span>
              <span>
                {formatCurrency(purchaseOrder.note.transportationCost || 0)}
              </span>
            </div>
            <div className='flex justify-between'>
              <span>Packaging Cost:</span>
              <span>
                {formatCurrency(purchaseOrder.note.packagingCost || 0)}
              </span>
            </div>
            <div className='flex justify-between'>
              <span>Miscellaneous Cost:</span>
              <span>{formatCurrency(purchaseOrder.note.miscCost || 0)}</span>
            </div>
            <div className='flex justify-between font-semibold border-t border-brand-20 pt-2'>
              <span>Current Grand Total:</span>
              <span>
                {formatCurrency(
                  (purchaseOrder.totalAmount || 0) +
                    (purchaseOrder.note.transportationCost || 0) +
                    (purchaseOrder.note.packagingCost || 0) +
                    (purchaseOrder.note.miscCost || 0)
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
        {/* Cost Fields */}
        <div className='space-y-4'>
          <h4 className='text-base font-medium text-gray-90'>
            Update Cost Information
          </h4>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='transportationCost'>
                Transportation Cost (₦) *
              </Label>
              <Controller
                name='transportationCost'
                control={control}
                rules={{
                  required: 'Transportation cost is required',
                  min: { value: 0, message: 'Cost cannot be negative' },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    type='number'
                    step='0.01'
                    min='0'
                    placeholder='0.00'
                    error={fieldState?.error?.message}
                    onChange={e => field.onChange(Number(e.target.value))}
                  />
                )}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='packagingCost'>Packaging Cost (₦) *</Label>
              <Controller
                name='packagingCost'
                control={control}
                rules={{
                  required: 'Packaging cost is required',
                  min: { value: 0, message: 'Cost cannot be negative' },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    type='number'
                    step='0.01'
                    min='0'
                    placeholder='0.00'
                    error={fieldState?.error?.message}
                    onChange={e => field.onChange(Number(e.target.value))}
                  />
                )}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='miscellaneousCost'>
                Miscellaneous Cost (₦) *
              </Label>
              <Controller
                name='miscellaneousCost'
                control={control}
                rules={{
                  required: 'Miscellaneous cost is required',
                  min: { value: 0, message: 'Cost cannot be negative' },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    type='number'
                    step='0.01'
                    min='0'
                    placeholder='0.00'
                    error={fieldState?.error?.message}
                    onChange={e => field.onChange(Number(e.target.value))}
                  />
                )}
              />
            </div>
          </div>

          {/* Updated Costs Summary */}
          {calculateTotalCosts() > 0 && (
            <div className='bg-success-5 p-3 rounded-lg'>
              <div className='flex justify-between text-sm'>
                <span className='text-success-70'>
                  Updated Additional Costs:
                </span>
                <span className='font-semibold text-success-90'>
                  {formatCurrency(calculateTotalCosts())}
                </span>
              </div>
              <div className='flex justify-between text-sm font-semibold border-t border-success-20 pt-2 mt-2'>
                <span className='text-success-70'>New Grand Total:</span>
                <span className='text-success-90'>
                  {formatCurrency(calculateGrandTotal())}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Location Fields */}
        <div className='space-y-4'>
          <h4 className='text-base font-medium text-gray-90'>
            Update Delivery Information
          </h4>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='loadingPoint'>Loading Point *</Label>
              <Controller
                name='loadingPoint'
                control={control}
                rules={{ required: 'Loading point is required' }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    placeholder='Enter loading point address'
                    error={fieldState?.error?.message}
                  />
                )}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='deliveryPoint'>Delivery Point *</Label>
              <Controller
                name='deliveryPoint'
                control={control}
                rules={{ required: 'Delivery point is required' }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    placeholder='Enter delivery point address'
                    error={fieldState?.error?.message}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Delivery Note */}
        <div className='space-y-2'>
          <Label htmlFor='deliveryNote'>Delivery Note *</Label>
          <Controller
            name='deliveryNote'
            control={control}
            rules={{
              required: 'Delivery note is required',
              minLength: {
                value: 10,
                message: 'Delivery note must be at least 10 characters',
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Textarea
                  {...field}
                  placeholder='Enter delivery instructions, special requirements, or other relevant notes'
                  rows={4}
                  maxLength={300}
                />
                {fieldState?.error && (
                  <p className='text-sm text-destructive-60'>
                    {fieldState.error.message}
                  </p>
                )}
              </>
            )}
          />
          <p className='text-xs text-gray-50'>
            {watchedValues.deliveryNote?.length || 0}/300 characters
          </p>
        </div>

        {/* Summary of Changes */}
        {isDirty && (
          <div className='bg-warning-5 border border-warning-20 rounded-lg p-3'>
            <h4 className='text-sm font-medium text-warning-80 mb-2'>
              Changes Summary
            </h4>
            <p className='text-sm text-warning-70'>
              You have unsaved changes to this purchase order note. Make sure to
              review your updates before saving.
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default PurchaseOrderNoteUpdateSheet;
